"use client";

import useMobile from "@/hooks/useMobile";
import { EventTrackingProps, L1ApopProps } from "@/types/common";
import trackClevertapEvents from "@/utils/trackClevertapEvents";
import dynamic from "next/dynamic";
import { useSearchParams } from "next/navigation";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import ProfileVisibilityNudge from "@/components/common/profileVisibilityNudge";

import AppliedPage from "./applied";
import DashboardHeader from "./dashboardHeader";
import DashboardTabs from "./dashboardTabs";
import {
  APPLIED,
  RECOMMENDED,
  SAVED,
} from "./dashboardTabs/constants/dashboardTabs";
import noResultData from "./noresult/utils/noResultData.json";
import { RecommendedDashboardPageDataType } from "./recommended/types";
import {
  DashboardContextProps,
  DashboardDataProps,
  PageSpecificDataProps,
  cleverTapEventTypesProps,
  cleverTapPropertyValuesProps,
  notificationData,
} from "./types";

const RecommendedPage = dynamic(() => import("./recommended"), {
  ssr: false,
});

const SavedPage = dynamic(() => import("./saved"), {
  ssr: false,
});

const NoResults = dynamic(() => import("./noresult"), {
  ssr: false,
});

const Apop = dynamic(() =>
  import("@monsterindia/l1-apop").then(
    (mod) => mod.Apop as unknown as React.FC<L1ApopProps>,
  ),
);

const ApplyReplyFlow = dynamic(() =>
  import("@monsterindia/features").then(
    (mod) =>
      mod.ApplyReplyFlow as unknown as React.FC<{
        isMobile: boolean;
        notificationData: notificationData;
        setNotificationData?: (param: null | notificationData) => void;
      }>,
  ),
);

interface DashboardPropsType {
  host: string;
  dashboardData: DashboardDataProps;
  isMobileServer: boolean;
}

const DashboardContext = createContext<DashboardContextProps>({
  isMobile: false,
  host: "",
  jobServerData: {} as DashboardDataProps,
  handleEventTracking: (event: EventTrackingProps) => {},
  cleverTapPropertyValues: {
    applied: "",
    recommended: "",
    saved: "",
  },
});

export function useDashboardContext() {
  return useContext(DashboardContext);
}

const DashboardPage = ({
  host,
  dashboardData,
  isMobileServer,
}: DashboardPropsType) => {
  const isMobile = useMobile(isMobileServer);
  const searchParams = useSearchParams();
  const [activeTab, setActiveTab] = useState(dashboardData.activeTab);
  const [clientTabSwitch, setClientTabSwitch] = useState(false);
  const [recommendedDashboardData, setRecommendedDashboardData] =
    useState<RecommendedDashboardPageDataType>({
      multiSelectCount: 0,
    });
  const [noJobs, setNoJobs] = useState(() => {
    let noJobsPage = 0;
    if (activeTab === APPLIED || activeTab === SAVED) {
      noJobsPage = dashboardData.jobData?.data?.length;
    } else if (activeTab === RECOMMENDED) {
      noJobsPage = Object.keys(dashboardData.jobData).reduce(
        (acc: number, data: string) =>
          acc + (dashboardData.jobData[data]?.data?.length || 0),
        0,
      );
    }
    return noJobsPage;
  });
  const [showL1Apop, setShowL1Apop] = useState(false);
  const [notificationData, setNotificationData] =
    useState<notificationData | null>(null);
  const dashboardHeaderRef = useRef<HTMLDivElement>(null);
  const [windowData, setWindowData] = useState({
    recaptchaV2Switch: false,
    isSyndicateQuickApplyEnabled: false,
    bulkApplyMultiSelect: "",
  });

  const jobServerData: DashboardDataProps = {
    jobData: dashboardData.jobData || {},
    autoApplyData: dashboardData.autoApplyData || {},
    profilePersonalDetails: dashboardData.profilePersonalDetails || {},
    activeTab: activeTab,
    clientTabSwitch: clientTabSwitch,
    siteProps: dashboardData.siteProps,
    sitePropsProperties: dashboardData.sitePropsProperties || {},
    validateProfileData: dashboardData.validateProfileData || {},
    outreachSubscriptionData:
      dashboardData.outreachSubscriptionData || undefined,
    dashboardHeaderRef: dashboardHeaderRef,
    windowData: windowData,
  };
  const pageSpecificData: PageSpecificDataProps = {
    recommendedDashboardData: recommendedDashboardData,
    setRecommendedDashboardData: setRecommendedDashboardData,
  };

  const cleverTapPropertyValues: cleverTapPropertyValuesProps = {
    applied: "Dashboard Applied",
    recommended: "Dashboard Recommended",
    saved: "Dashboard Saved",
  };

  const cleverTapEventTypes: cleverTapEventTypesProps = {
    eventPush: "eventPush",
    splTrackEvent: "splTrackEvent",
    getPlatform: "getPlatform",
  };

  const L1ApopData = {
    actionType: "EMPLOYMENT",
  };

  const handleEventTracking = useCallback(
    (event: EventTrackingProps) => {
      const { eventName, eventType, eventProps, action } = event;
      if (eventProps && !eventProps["page_name"]) {
        eventProps["page_name"] = "Dashboard Applied";
      }

      switch (eventType) {
        case cleverTapEventTypes.eventPush:
          trackClevertapEvents.eventPush(eventName, eventProps);
          break;
        case cleverTapEventTypes.splTrackEvent:
          trackClevertapEvents.splTrackEvent(action, eventName, eventProps);
          break;
        default:
          break;
      }
    },
    [isMobile, cleverTapEventTypes, trackClevertapEvents],
  );
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const openAddEmployment = searchParams.get("apop") === "addEmployment";
    if (openAddEmployment) {
      setShowL1Apop(true);
    }
    setWindowData({
      recaptchaV2Switch:
        window?._siteProps_?.propertiesMap?.recaptchaV2Switch &&
        window?._siteProps_.propertiesMap.recaptchaV2Switch === "true",
      bulkApplyMultiSelect:
        window?._siteProps_?.propertiesMap?.bulkApplyMultiSelect,
      isSyndicateQuickApplyEnabled:
        window?._siteProps_?.propertiesMap?.syndicatedQuickApplyEnabled,
    });
  }, []);

  const contextValue = useMemo(
    () => ({
      host,
      isMobile,
      jobServerData,
      handleEventTracking,
      cleverTapPropertyValues,
    }),
    [isMobile, handleEventTracking],
  );

  const getPage = () => {
    switch (activeTab) {
      case APPLIED:
        return <AppliedPage />;
      case RECOMMENDED:
        return (
          <RecommendedPage
            setRecommendedDashboardData={setRecommendedDashboardData}
          />
        );
      case SAVED:
        return <SavedPage />;
      default:
        return (
          <RecommendedPage
            setRecommendedDashboardData={setRecommendedDashboardData}
          />
        );
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const activeTabClient = params.get("activeTab");
    if (jobServerData.activeTab === activeTabClient) {
      setNoJobs(() => {
        let noJobsPage = 0;
        if (
          jobServerData.activeTab === "applied" ||
          jobServerData.activeTab === "saved"
        ) {
          noJobsPage = jobServerData.jobData?.data?.length;
        } else if (activeTab === "recommended") {
          noJobsPage = Object.keys(jobServerData.jobData).reduce(
            (acc: number, data: string) =>
              acc + (jobServerData.jobData[data]?.data?.length || 0),
            0,
          );
        }
        return noJobsPage;
      });
    }
  }, [dashboardData, searchParams]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("apop") === "applyReplyNudge") {
      setNotificationData({
        reply: urlParams.get("reply"),
        apply: urlParams.get("apply"),
        messageId: urlParams.get("messageId"),
        messageCode: urlParams.get("messageCode"),
        corpId: urlParams.get("corpId"),
        folderId: urlParams.get("folderId"),
        outreachMailEventId: urlParams.get("outreachMailEventId"),
      });
    }
  }, [searchParams]);

  return (
    <>
      {showL1Apop && (
        <Apop
          data={L1ApopData as { actionType: string }}
          openApop={showL1Apop}
          closeApop={() => setShowL1Apop(false)}
          flowType="L1_APOP"
          showEmailVerification={false}
          type="EMPLOYMENT"
          onApopComplete={() => setShowL1Apop(false)}
          isRecaptchaEnabled={windowData.recaptchaV2Switch || false}
        />
      )}
      {notificationData ? (
        <ApplyReplyFlow
          isMobile={isMobile}
          notificationData={{
            reply: notificationData?.reply || null,
            apply: notificationData?.apply || null,
            messageId: notificationData?.messageId || null,
            messageCode: notificationData?.messageCode || null,
            corpId: notificationData?.corpId || null,
            folderId: notificationData?.folderId || null,
            outreachMailEventId: notificationData?.outreachMailEventId || null,
          }}
          setNotificationData={setNotificationData}
        />
      ) : null}
      <DashboardContext.Provider value={contextValue}>
        <div
          className="md:!sticky md:!top-0 md:!z-[9] md:!mb-6 md:!bg-white"
          id="dashboard-header"
          ref={dashboardHeaderRef}
        >
          <ProfileVisibilityNudge
            handleEventTracking={handleEventTracking}
            pageName="Dashboard"
          />
          <DashboardHeader
            isMobile={isMobile}
            activeTab={jobServerData.activeTab}
            jobPreferenceData={
              jobServerData.profilePersonalDetails?.jobPreferenceSection
            }
            doNotRender={true}
          />
          <DashboardTabs
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            setClientTabSwitch={setClientTabSwitch}
            pageSpecificData={pageSpecificData}
            isMobile={isMobile}
          />
        </div>
        {noJobs > 0 ? (
          <>{getPage()}</>
        ) : (
          <NoResults
            query={
              noResultData[dashboardData.activeTab as keyof typeof noResultData]
            }
          />
        )}
      </DashboardContext.Provider>
    </>
  );
};

export default React.memo(DashboardPage);
